import { toast } from '@bozic/ui/toast';
import AsyncLock from 'async-lock';
import { UserManager, type User } from 'oidc-client-ts';

import { getConfig } from '@/features/environment/config';

import Logger from '@/shared/logger';

import store from '@/ducks/store';

import { signOut as signOutAction, updateState } from '../slice/authSlice';

const envConfig = getConfig();

export const userManager = new UserManager({
  authority: envConfig?.AUTH_URL ?? '',
  client_id: envConfig?.CLIENT_ID ?? '',
  redirect_uri: `${envConfig?.PAGE_URL}/callback/login`,
  silent_redirect_uri: `${envConfig?.PAGE_URL}/callback/refresh`,
  post_logout_redirect_uri: `${envConfig?.PAGE_URL}/callback/logout`,
  automaticSilentRenew: false,
  response_type: 'code',
  scope: 'openid profile roles organization_api identity_api insurance_api offline_access',
  filterProtocolClaims: true,
  loadUserInfo: true
});

userManager.events.addUserLoaded(() => {
  store.dispatch(updateState());
});

userManager.events.addUserSignedOut(() => {
  store.dispatch(signOutAction());
});

let signInPromise: Promise<void> | null;
export function signIn(): Promise<void> {
  if (signInPromise == null) {
    // reset in finally: a success-only reset would cache a rejected promise forever
    signInPromise = userManager.signinRedirect().finally(() => {
      signInPromise = null;
    });
  }
  return signInPromise;
}

let signOutPromise: Promise<void> | null;
export function signOut(): Promise<void> {
  if (signOutPromise == null) {
    localStorage.removeItem('organizationName');
    localStorage.removeItem('agency');
    localStorage.removeItem('insurancePlan');
    signOutPromise = userManager.signoutRedirect().finally(() => {
      signOutPromise = null;
    });
  }
  return signOutPromise;
}

type AuthenticatedUser = User | null | void;

let refreshTokenPromise: Promise<AuthenticatedUser> | null;
export function refreshToken(): Promise<AuthenticatedUser> {
  if (refreshTokenPromise == null) {
    refreshTokenPromise = userManager
      .signinSilent({})
      .catch(async (reason: unknown) => {
        Logger.trace('Silent token refresh failed, signing out', {
          level: 'Warning',
          tag: 'oidc',
          extra: { error: reason }
        });
        try {
          await signOut();
        } catch (signOutError) {
          // signoutRedirect can fail for the same reason the refresh did (IdP
          // unreachable) — swallow it so the shared refresh promise resolves
          // null instead of caching a rejection that poisons every later 401
          Logger.trace('Sign-out after failed token refresh also failed', {
            level: 'Error',
            tag: 'oidc',
            sentryLogging: true,
            extra: { error: signOutError, refreshError: reason }
          });
        }
        return null;
      })
      .finally(() => {
        refreshTokenPromise = null;
      });
  }
  return refreshTokenPromise;
}

export const handleOIDCError = (error: Error, navigateCb: () => void) => {
  if (error.message.includes('iat')) {
    toast.error(
      'Your system clock is incorrect. Please set your clock to the current date and time.',
      // duration 0 = no auto-dismiss; the user must fix their clock before continuing
      { duration: 0 }
    );
  } else if (error.message.includes('No matching state found in storage')) {
    toast.error('Please login with your credentials', {
      position: 'top-right'
    });
    navigateCb();
  } else {
    console.error(error);
  }
};

const lock = new AsyncLock();
export async function getAuthenticatedUser(): Promise<User | void> {
  return lock.acquire('auth-token-check', async (): Promise<User | void> => {
    let user: AuthenticatedUser = await userManager.getUser();
    if (!user?.access_token) {
      console.info('getAuthenticatedUser:no-user');
      return signOut();
    }

    if (user.expires_in && user.expires_in <= 15) {
      user = await refreshToken();
      if (!user || !user.access_token) {
        console.info('getAuthenticatedUser:no-user-after-refresh');
        return signOut();
      }
    }

    return user;
  });
}
